import React, { useState, useEffect } from 'react';
import { Wallet as WalletIcon, Plus, Gift, CreditCard, ArrowUpRight, ArrowDownRight, Award, LogOut, Sparkles, Users, Copy, Share2, Banknote } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { walletAPI, notificationsAPI, referralAPI } from '../lib/api';
import { formatCurrency, formatRelativeTime, getVipTierColor } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function WalletPage() {
  const { user, logout } = useAuth();
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addAmount, setAddAmount] = useState('');
  const [isAddingFunds, setIsAddingFunds] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [balanceRes, transRes, notifsRes, refRes, wRes] = await Promise.all([
          walletAPI.getBalance(),
          walletAPI.getTransactions(),
          notificationsAPI.getAll(),
          referralAPI.getStats().catch(() => ({ data: null })),
          walletAPI.getWithdrawals().catch(() => ({ data: [] })),
        ]);
        setBalance(balanceRes.data);
        setTransactions(transRes.data);
        setNotifications(notifsRes.data);
        setReferralStats(refRes.data);
        setWithdrawals(wRes.data);
      } catch (error) {
        console.error('Error fetching wallet data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAddFunds = async () => {
    const amount = parseFloat(addAmount);
    if (isNaN(amount) || amount <= 0) return;
    setIsAddingFunds(true);
    try {
      const res = await walletAPI.addFunds(amount);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
      setIsAddingFunds(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) return;
    setIsWithdrawing(true);
    try {
      await walletAPI.withdraw(amount);
      toast.success(`Withdrawal of $${amount} is processing`);
      const [bRes, tRes, wRes] = await Promise.all([walletAPI.getBalance(), walletAPI.getTransactions(), walletAPI.getWithdrawals()]);
      setBalance(bRes.data);
      setTransactions(tRes.data);
      setWithdrawals(wRes.data);
      setWithdrawAmount('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setIsWithdrawing(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(balance?.referral_code || '');
    toast.success('Referral code copied!');
  };

  const shareReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${balance?.referral_code}`;
    if (navigator.share) {
      navigator.share({ title: 'Join MMarketing', text: 'Get bonus points when you sign up!', url: link });
    } else {
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-8">
      {/* Header */}
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-[#b8ff00] to-[#00ff88] rounded-2xl flex items-center justify-center">
                <WalletIcon className="text-black" size={28} />
              </div>
              <div>
                <h1 className="font-display font-bold text-2xl text-zinc-900">Wallet</h1>
                <p className="text-zinc-500">Manage your funds & rewards</p>
              </div>
            </div>
            <Button variant="ghost" onClick={logout} className="text-zinc-500 hover:text-zinc-900" data-testid="logout-btn">
              <LogOut size={18} className="mr-2" /> Logout
            </Button>
          </div>

          {/* Balance Card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl p-6 text-white">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-zinc-400 text-sm mb-1">Available Balance</p>
                <p className="font-display font-bold text-4xl gradient-text" data-testid="wallet-balance">
                  {formatCurrency(balance?.balance || 0)}
                </p>
              </div>
              <div className="flex gap-2">
                {/* Withdraw Button (vendors) */}
                {user?.role === 'vendor' && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-zinc-600 text-white hover:bg-zinc-700 rounded-full" data-testid="withdraw-btn">
                        <Banknote size={18} className="mr-1" /> Withdraw
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-zinc-200 rounded-2xl">
                      <DialogHeader>
                        <DialogTitle className="font-display text-zinc-900">Withdraw Funds</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <p className="text-sm text-zinc-500">Available: <strong className="text-zinc-900">{formatCurrency(balance?.balance || 0)}</strong></p>
                        <Input
                          type="number" placeholder="Enter withdrawal amount"
                          value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)}
                          className="h-12 bg-zinc-50 border-zinc-200 rounded-xl" data-testid="withdraw-amount-input"
                        />
                        <Button onClick={handleWithdraw} disabled={isWithdrawing || !withdrawAmount || parseFloat(withdrawAmount) > (balance?.balance || 0)}
                          className="w-full h-12 bg-zinc-900 text-white font-bold rounded-xl" data-testid="confirm-withdraw-btn">
                          {isWithdrawing ? 'Processing...' : 'Request Withdrawal'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {/* Add Funds Button */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-[#b8ff00] text-black hover:bg-[#a8ef00] rounded-full" data-testid="add-funds-btn">
                      <Plus size={18} className="mr-1" /> Add Funds
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-zinc-200 rounded-2xl">
                    <DialogHeader>
                      <DialogTitle className="font-display text-zinc-900">Add Funds via Card</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-xl border border-blue-200">
                        <CreditCard size={18} className="text-blue-500 flex-shrink-0" />
                        <p className="text-xs text-blue-700">You'll be redirected to Stripe to securely pay with your credit or debit card.</p>
                      </div>
                      <Input
                        type="number" placeholder="Enter amount" min="1" step="0.01"
                        value={addAmount} onChange={(e) => setAddAmount(e.target.value)}
                        className="h-12 bg-zinc-50 border-zinc-200 rounded-xl" data-testid="add-funds-input"
                      />
                      <div className="flex gap-2">
                        {[10, 25, 50, 100].map((amt) => (
                          <Button key={amt} variant="outline" size="sm" onClick={() => setAddAmount(amt.toString())} className="flex-1 rounded-full">${amt}</Button>
                        ))}
                      </div>
                      <Button onClick={handleAddFunds} disabled={isAddingFunds || !addAmount || parseFloat(addAmount) <= 0}
                        className="w-full h-12 bg-gradient-to-r from-[#b8ff00] to-[#00ff88] text-black font-bold rounded-xl" data-testid="confirm-add-funds-btn">
                        {isAddingFunds ? 'Redirecting to Stripe...' : `Pay $${addAmount || '0'} with Card`}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Points & Tier */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={16} className="text-[#b8ff00]" />
                  <span className="text-xs text-zinc-400 uppercase tracking-wider">Points</span>
                </div>
                <p className="font-display font-bold text-2xl" data-testid="points-display">{balance?.points?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Award size={16} style={{ color: getVipTierColor(balance?.vip_tier || 'bronze') }} />
                  <span className="text-xs text-zinc-400 uppercase tracking-wider">VIP Tier</span>
                </div>
                <p className="font-display font-bold text-2xl capitalize" style={{ color: getVipTierColor(balance?.vip_tier || 'bronze') }} data-testid="vip-tier-display">{balance?.vip_tier || 'Bronze'}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Tabs defaultValue="transactions" className="w-full">
          <TabsList className="bg-white border border-zinc-200 rounded-full p-1 mb-6 flex-wrap">
            <TabsTrigger value="transactions" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white">Transactions</TabsTrigger>
            {user?.role === 'vendor' && (
              <TabsTrigger value="withdrawals" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white">Withdrawals</TabsTrigger>
            )}
            <TabsTrigger value="referrals" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white">Referrals</TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-full data-[state=active]:bg-zinc-900 data-[state=active]:text-white">Notifications</TabsTrigger>
          </TabsList>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <div className="grid grid-cols-3 gap-4 mb-6">
              <button className="p-4 bg-white rounded-2xl border border-zinc-200 hover:border-zinc-300 transition-colors text-center" data-testid="send-funds-btn">
                <div className="w-12 h-12 mx-auto mb-2 bg-[#b8ff00]/10 rounded-xl flex items-center justify-center">
                  <ArrowUpRight className="text-[#00aa55]" size={24} />
                </div>
                <span className="text-sm font-medium text-zinc-700">Send</span>
              </button>
              <button className="p-4 bg-white rounded-2xl border border-zinc-200 hover:border-zinc-300 transition-colors text-center" data-testid="receive-funds-btn">
                <div className="w-12 h-12 mx-auto mb-2 bg-violet-500/10 rounded-xl flex items-center justify-center">
                  <ArrowDownRight className="text-violet-500" size={24} />
                </div>
                <span className="text-sm font-medium text-zinc-700">Receive</span>
              </button>
              <button className="p-4 bg-white rounded-2xl border border-zinc-200 hover:border-zinc-300 transition-colors text-center" data-testid="history-btn">
                <div className="w-12 h-12 mx-auto mb-2 bg-zinc-100 rounded-xl flex items-center justify-center">
                  <CreditCard className="text-zinc-500" size={24} />
                </div>
                <span className="text-sm font-medium text-zinc-700">History</span>
              </button>
            </div>

            {transactions.length > 0 ? (
              <div className="bg-white rounded-2xl border border-zinc-200 divide-y divide-zinc-100">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.type === 'credit' || tx.type === 'reward' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {tx.type === 'credit' || tx.type === 'reward' ? <ArrowDownRight className="text-green-600" size={18} /> : <ArrowUpRight className="text-red-600" size={18} />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">{tx.description}</p>
                        <p className="text-xs text-zinc-500">{formatRelativeTime(tx.created_at)}</p>
                      </div>
                    </div>
                    <span className={`font-mono font-bold ${tx.type === 'credit' || tx.type === 'reward' ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.type === 'credit' || tx.type === 'reward' ? '+' : '-'}{formatCurrency(tx.amount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-zinc-200">
                <CreditCard className="mx-auto mb-3 text-zinc-300" size={32} />
                <p className="text-zinc-500">No transactions yet</p>
              </div>
            )}
          </TabsContent>

          {/* Withdrawals Tab */}
          <TabsContent value="withdrawals">
            {withdrawals.length > 0 ? (
              <div className="bg-white rounded-2xl border border-zinc-200 divide-y divide-zinc-100">
                {withdrawals.map((w) => (
                  <div key={w.id} className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-orange-100">
                        <Banknote className="text-orange-600" size={18} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900">Withdrawal #{w.id.slice(0, 8)}</p>
                        <p className="text-xs text-zinc-500">{formatRelativeTime(w.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-mono font-bold text-orange-600">{formatCurrency(w.amount)}</span>
                      <span className={`block text-xs px-2 py-0.5 rounded-full font-medium mt-1 ${w.status === 'completed' ? 'bg-green-100 text-green-700' : w.status === 'processing' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'}`}>{w.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-zinc-200">
                <Banknote className="mx-auto mb-3 text-zinc-300" size={32} />
                <p className="text-zinc-500">No withdrawals yet</p>
              </div>
            )}
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-2 mb-4"><Gift size={20} /><span className="font-semibold">Your Referral Code</span></div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3">
                    <span className="font-mono font-bold text-2xl tracking-wider text-white drop-shadow-sm" data-testid="referral-code-display">{balance?.referral_code || 'Loading...'}</span>
                  </div>
                  <Button onClick={copyReferralCode} variant="ghost" className="h-12 w-12 bg-white/20 hover:bg-white/30 rounded-xl"><Copy size={20} /></Button>
                  <Button onClick={shareReferralLink} variant="ghost" className="h-12 w-12 bg-white/20 hover:bg-white/30 rounded-xl"><Share2 size={20} /></Button>
                </div>
                <p className="text-sm text-white/80">Share your code and earn 100 points for each friend who joins!</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-zinc-200 p-4">
                  <div className="flex items-center gap-2 mb-2"><Users size={16} className="text-violet-500" /><span className="text-xs text-zinc-500 uppercase">Referrals</span></div>
                  <p className="font-display font-bold text-2xl text-zinc-900">{referralStats?.referral_count || 0}</p>
                </div>
                <div className="bg-white rounded-2xl border border-zinc-200 p-4">
                  <div className="flex items-center gap-2 mb-2"><Sparkles size={16} className="text-[#b8ff00]" /><span className="text-xs text-zinc-500 uppercase">Points Earned</span></div>
                  <p className="font-display font-bold text-2xl text-zinc-900">{referralStats?.total_bonus_earned || 0}</p>
                </div>
              </div>
              {referralStats?.referred_users?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-zinc-900 mb-3">People you've referred</h3>
                  <div className="bg-white rounded-2xl border border-zinc-200 divide-y divide-zinc-100">
                    {referralStats.referred_users.map((u, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center"><span className="text-violet-600 font-semibold">{u.name?.[0] || '?'}</span></div>
                          <span className="font-medium text-zinc-900">{u.name}</span>
                        </div>
                        <span className="text-xs text-zinc-500">{formatRelativeTime(u.joined)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            {notifications.length > 0 ? (
              <div className="space-y-3">
                {notifications.map((notif) => (
                  <div key={notif.id} className={`p-4 rounded-2xl border ${notif.is_read ? 'bg-white border-zinc-200' : 'bg-[#b8ff00]/5 border-[#b8ff00]/20'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-sm text-zinc-900">{notif.title}</p>
                        <p className="text-xs text-zinc-500 mt-1">{notif.message}</p>
                      </div>
                      {!notif.is_read && <span className="w-2 h-2 bg-[#b8ff00] rounded-full" />}
                    </div>
                    <p className="text-xs text-zinc-400 mt-2">{formatRelativeTime(notif.created_at)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-zinc-200">
                <p className="text-zinc-500">No notifications</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
